import { z } from 'zod/v4';
import { COMPANIES_FILTER_MAX_LENGTH } from './companies-list.js';

export const NetworkGraphParamsSchema = z.object({
  id: z.string().min(1),
});

export type NetworkGraphParams = z.infer<typeof NetworkGraphParamsSchema>;

export const NetworkGraphLinkParamsSchema = z.object({
  graphId: z.string().min(1),
  linkId: z.string().min(1),
});

export type NetworkGraphLinkParams = z.infer<
  typeof NetworkGraphLinkParamsSchema
>;

/**
 * A node's canvas position on the supplier network graph. Both coordinates
 * are optional and other keys are stripped, as the legacy schema's
 * `additionalProperties: false` did.
 */
const NetworkGraphNodePositionSchema = z.object({
  x: z.number().optional(),
  y: z.number().optional(),
});

/**
 * Body of `PATCH /api/network-companies/graph/:id/nodes` — the legacy
 * `schemas/v3/request/network-companies.js#bulkPatchNodes`: at least one node,
 * each with its id and new position.
 */
export const PatchNetworkGraphNodesBodySchema = z
  .array(
    z.object({
      id: z.string().min(1),
      position: NetworkGraphNodePositionSchema,
    }),
  )
  .min(1);

export type PatchNetworkGraphNodesBody = z.infer<
  typeof PatchNetworkGraphNodesBodySchema
>;

/**
 * Body of `POST /api/network-companies/graph/:id/links` — the legacy
 * `schemas/v3/request/network-companies.js#createLink`. Other keys are
 * stripped.
 */
export const CreateNetworkGraphLinkBodySchema = z.object({
  originNodeId: z.string().min(1),
  destinationNodeId: z.string().min(1),
});

export type CreateNetworkGraphLinkBody = z.infer<
  typeof CreateNetworkGraphLinkBodySchema
>;

/** A directed link between two nodes of a supplier network graph. */
export const NetworkGraphLinkSchema = z.object({
  id: z.string(),
  networkCompanyGraphId: z.string(),
  originNodeId: z.string(),
  destinationNodeId: z.string(),
});

export type NetworkGraphLink = z.infer<typeof NetworkGraphLinkSchema>;

/**
 * Answer of `POST /api/network-companies/graph`: the company's graph id — the
 * graph just built, or the one it already had. Read the graph itself with
 * `GET /graph`; the legacy route answered the whole graph view, which its only
 * caller ignored.
 */
export const NetworkGraphCreatedSchema = z.object({
  id: z.string(),
});

export type NetworkGraphCreated = z.infer<typeof NetworkGraphCreatedSchema>;

/**
 * Query of `GET /api/network-companies/graph` — the legacy `getGraph`
 * querystring. `filter` is JSON: `{ facilityProcesses?, name?, country? }`
 * (see `listNetworkGraphNodeRowsDb`).
 */
export const NetworkGraphQuerySchema = z.object({
  filter: z.string().max(COMPANIES_FILTER_MAX_LENGTH).optional(),
});

export type NetworkGraphQuery = z.infer<typeof NetworkGraphQuerySchema>;

const NetworkGraphFacilityProcessSchema = z.object({
  id: z.string(),
  name: z.string(),
  productionProcessId: z.string().nullable(),
});

const NetworkGraphProductionProcessSchema = z.object({
  id: z.string(),
  name: z.string(),
  position: z.number().nullable(),
  color: z.string().nullable(),
  iconColor: z.string().nullable(),
  description: z.string().nullable(),
});

/** The node's company, image urls made absolute as the legacy `buildCompany` did. */
const NetworkGraphNodeCompanySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  featuredImageFileUrl: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  imageList: z.array(z.record(z.string(), z.unknown())),
});

export const NetworkGraphNodeSchema = z.object({
  id: z.string(),
  networkCompanyGraphId: z.string(),
  companyId: z.string(),
  productionProcessId: z.string().nullable(),
  /** The stored canvas position; null until the node is placed. */
  position: z.record(z.string(), z.unknown()).nullable(),
  company: NetworkGraphNodeCompanySchema,
  /** null for a facility process that no longer exists, as in legacy. */
  facilityProcesses: z.array(NetworkGraphFacilityProcessSchema.nullable()),
});

export type NetworkGraphNode = z.infer<typeof NetworkGraphNodeSchema>;

/**
 * The nodes of one production process. `id` is the layer's 1-based place in
 * the order the nodes were read, as a string (the canvas uses it as a parent
 * node id); `position` is the production process's, or 20000 without one.
 */
export const NetworkGraphLayerSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  position: z.number(),
  productionProcess: NetworkGraphProductionProcessSchema.nullable(),
  nodes: z.array(NetworkGraphNodeSchema),
});

export type NetworkGraphLayer = z.infer<typeof NetworkGraphLayerSchema>;

/** The company's graph: its layers, highest position first, and the links between shown nodes. */
export const NetworkGraphSchema = z.object({
  id: z.string(),
  layers: z.array(NetworkGraphLayerSchema),
  links: z.array(NetworkGraphLinkSchema),
});

export type NetworkGraph = z.infer<typeof NetworkGraphSchema>;
